import { CombineOperator, SubTagSearchType, TagSearchType, TopLevelTagSearchType, UserTag } from '../types';
import { GoogleTagEvent, logWithGoogleTag, logWithGoogleTagAndTopicOrganization } from '../helpers/api/api';
import { LitElement, css, html } from 'lit';
import { customElement, property, query, state } from 'lit/decorators';

import { FilterChips } from './filter-chips';
import { FormInput } from './theme/form-input';
import { StateController } from '../helpers/state/state-management/state-controller';
import { getCategoryIcon } from '../helpers/markers';
import { getVisibleTextColor } from '../helpers/colors';
import { isDefined } from '../helpers/utils';
import topicData from '../helpers/state/topic-data';
import translationData from '../helpers/state/translation-data';

@customElement('advanced-filtering-modal')
export class AdvancedFilteringModal extends LitElement {

    @query('#containWords')
    containWordsElement!: FormInput;

    @query('#or')
    orFilterOption?: HTMLInputElement;

    @query('#and')
    andFilterOption?: HTMLInputElement;

    @property({ type: Boolean })
    challengeFilter = false;

    @state()
    selectedCategories: string[] = [];

    @state()
    selectedTopTags: TopLevelTagSearchType[] = [];

    @state()
    selectedSubTags: SubTagSearchType[] = [];

    @state()
    selectedTags: TagSearchType = { operator: CombineOperator.Or, tags: []};

    @state()
    keywordsList: string[] = [];

    @state()
    openSubtag?: UserTag;

    @state()
    hasCustomTagsBehavior: boolean = false;

    @state()
    showCustomFilters: boolean = false;

    @state()
    topicTags: UserTag[] = [];

    private readonly bindTranslationState = new StateController(this, translationData);

    // listen for changes to this.clearSearch
    // if true, clear all filters
    async firstUpdated() {
        this.topicTags = topicData.topLevelUserTags;
        this.customizeStyles();
    }

    async changeTopLevelOperator(CombineOperator: CombineOperator) {
        this.selectedTags.operator = CombineOperator;
        this.requestUpdate();
    }

    async changeSubtagOperator(topLevelTag: string, CombineOperator: CombineOperator) {
        const subtag = this.selectedTags.tags.find((tag: any) => tag.topLevelTag === topLevelTag) as SubTagSearchType;
        if (isDefined(subtag)) {
            subtag.operator = CombineOperator;
        } else {
            this.selectedSubTags.push({ topLevelTag, operator: CombineOperator, subTags: [] });
            this.selectedTags.tags = [...this.selectedTopTags, ...this.selectedSubTags];
        }
        this.requestUpdate();
    }

    // if making changes here, you might also need to be making changes in filter-chips
    private handleChipClick(selectingTag = false, chipName: string, isSelectedCheckedInFilter = false, isSubTag = false, parentName?: string) {

        const logClick = () => {
            logWithGoogleTag(FilterChips.amplitude_callsite, GoogleTagEvent.CHIP_CLICKED, {
            chipName,
            isTag: selectingTag,
            topic_id: topicData.topic?._id?.$oid,
            });
        };

        const toggleTopTag = () => {
            if (this.selectedTopTags.some((t) => t.topLevelTag === chipName)) {
                this.selectedTopTags = this.selectedTopTags.filter((elem) => elem.topLevelTag !== chipName);
            } else if (chipName !== undefined) {
                this.selectedTopTags = [...this.selectedTopTags, { topLevelTag: chipName }];
                logWithGoogleTagAndTopicOrganization(FilterChips.amplitude_callsite, GoogleTagEvent.MAP_TAG_CLICKED, {
                    tag_name: chipName,
                });
            }
        };

        const toggleSubTag = () => {
            if (parentName === undefined) return;
            const parentTag = this.selectedSubTags.find((t) => t.topLevelTag === parentName);
            if (parentTag) {
                if (parentTag.subTags.includes(chipName)) {
                    parentTag.subTags = parentTag.subTags.filter((elem) => elem !== chipName);
                } else if (chipName !== undefined) {
                    parentTag.subTags = [...parentTag.subTags, chipName];
                    logWithGoogleTagAndTopicOrganization(FilterChips.amplitude_callsite, GoogleTagEvent.MAP_TAG_CLICKED, {
                        tag_name: chipName,
                    });
                }
                // if the parent tag has no sub tags, remove it from selectedSubTags
                if (parentTag.subTags.length === 0) {
                    this.selectedSubTags = this.selectedSubTags.filter((elem) => elem.topLevelTag !== parentName);
                }
            } else {
                this.selectedSubTags = [...this.selectedSubTags, { topLevelTag: parentName, operator: CombineOperator.Or, subTags: [chipName] }];
                logWithGoogleTagAndTopicOrganization(FilterChips.amplitude_callsite, GoogleTagEvent.MAP_TAG_CLICKED, {
                    tag_name: chipName,
                });
            }
        };

        const toggleCategory = () => {
            if (this.selectedCategories.includes(chipName)) {
                this.selectedCategories = this.selectedCategories.filter((elem) => elem !== chipName);
            } else if (chipName !== undefined) {
                this.selectedCategories = [...this.selectedCategories, chipName];
                logWithGoogleTagAndTopicOrganization(FilterChips.amplitude_callsite, GoogleTagEvent.MAP_CATEGORY_CLICKED, {
                    category_name: chipName,
                });
            }
        };
        logClick();

        if (selectingTag && !isSubTag) {
            toggleTopTag();
        } else if (selectingTag && isSubTag && parentName) {
            toggleSubTag();
        } else {
            toggleCategory();
        }

        if (this.selectedSubTags.length === 0) {
            this.selectedTags.tags = this.selectedTopTags;
        } else {
            this.selectedTags.tags = [...this.selectedTopTags, ...this.selectedSubTags];
        }

        // if subtag click, reorder this.topictags.usertags that are subtags so that the strings listed in this.selectedSubTags are first
        if (isSubTag) {
            const subtag = this.topicTags.find((tag: UserTag) => tag.name === parentName);
            if (subtag) {
                const subtagIndex = this.topicTags.findIndex((tag: UserTag) => tag.name === parentName);
                const newSubtag = {
                    ...subtag,
                    tags: subtag.tags?.sort((a: UserTag, b: UserTag) => {
                        const aIndex = this.selectedSubTags
                            .find((tag: SubTagSearchType) => tag.topLevelTag === parentName)
                            ?.subTags.findIndex((sub: string) => sub === a.name) ?? -1;
                        const bIndex =
                            this.selectedSubTags
                                .find((tag: SubTagSearchType) => tag.topLevelTag === parentName)
                                ?.subTags?.findIndex((sub: string) => sub === b.name) ?? -1;

                        if (aIndex === -1) return 1;
                        if (bIndex === -1) return -1;
                        return aIndex - bIndex;
                    })
                };
                this.topicTags[subtagIndex] = newSubtag;
            }
        }

    this.dispatchEvent(
        new CustomEvent('on-tags-changed', {
            detail: {
                keywordsList: this.keywordsList ?? [],
                selectedCategories: this.selectedCategories,
                selectedTags: this.selectedTags,
                selectedSubTags: this.selectedSubTags,
                selectedTopTags: this.selectedTopTags
            }
        })
    );


        // rerender this chip
        this.requestUpdate();
    }

    private async handleSubmitWords(inputValue: string) {
        const words = inputValue.split(' ').filter((word) => word.trim().length > 0);
        this.keywordsList.push(...words);
        this.keywordsList = [...new Set(this.keywordsList)];
        this.containWordsElement.formInputElement.value = '';

        if (words.length > 0) {
            logWithGoogleTagAndTopicOrganization(FilterChips.amplitude_callsite, GoogleTagEvent.MAP_KEYWORD_FILTER_APPLIED, {
                search_query: words.join(' '),
            });
        }
    }

    resetFilters() {
        this.keywordsList = [];
        this.containWordsElement.formInputElement.value = '';
        if (this.orFilterOption && this.andFilterOption) {
            this.orFilterOption.checked = false;
            this.andFilterOption.checked = true;
        }
        this.selectedCategories = [];
        this.selectedTopTags = [];
        this.selectedSubTags = [];
        this.selectedTags = { operator: CombineOperator.And, tags: []};

        this.orFilterOption?.click();

        this.requestUpdate();

        this.dispatchEvent(new CustomEvent(
            'clear-all-filters',
        ));
    }

    private submitFilters() {
        const detail = {
            keywordsList: this.keywordsList ?? [],
            selectedCategories: this.selectedCategories,
            selectedTags: this.selectedTags,
            selectedSubTags: this.selectedSubTags,
            selectedTopTags: this.selectedTopTags,
        };
        this.dispatchEvent(new CustomEvent(
            'on-advanced-search',
            {
                detail
            }
        ));
    }

    render() {
        return html`
            <div class="modalContent">
                <div class="modalBody">
                    ${topicData.topic?.hide_keyword_filters !== true && translationData.language === 'en' ? html`
                        <div>
                            <h4>Contain Words</h4>
                            <form-input
                                id="containWords"
                                type="text"
                                .hasAdd=${true}
                                placeholder="Enter words to include in your search"
                                @submit="${async (e: any) => await this.handleSubmitWords(e.detail)}">
                            </form-input>
                        </div>
                        <div class="keywords-container flexContainer">
                            ${this.keywordsList.length > 0
                                ? this.keywordsList.map((c) => {
                                    return html`
                                        <paper-chip
                                            label="${c}"
                                            selectable
                                        >
                                        </paper-chip>`;
                                })
                                : ''}
                        </div>
                    ` : ''}
                    ${topicData.topic?.hide_category_filters !== true ? html`
                        <h4><translated-span>Categories</translated-span></h4>
                        <div class="chips-container flexContainer">
                            ${topicData.categories.length > 0
                                ? topicData.categories.map((c) => {
                                    const isSelected = this.selectedCategories?.includes(c.name);
                                    const checkedClass = isSelected ? '' : 'displayNone';
                                    const iconClass = isSelected || !c.icon ? 'displayNone' : '';
                                    const translation = translationData.allTranslations.find((t) => t.original_text === c.name)?.translated_text;
                                    return html`
                                        <paper-chip
                                            label="${translation ?? c.name}"
                                            class="custom-chip"
                                            @click="${(e: any) =>
                                                this.handleChipClick(
                                                    false,
                                                    c.name,
                                                )}"
                                            selectable
                                        >
                                            <span class="chip-background" slot="avatar">
                                                <iron-icon
                                                    icon="icons:check"
                                                    class="${checkedClass}"
                                                ></iron-icon>
                                                <iron-icon src="${getCategoryIcon(c._id)}" class="${iconClass}"></iron-icon>
                                            </span>
                                        </paper-chip>
                                    `;
                                })
                                : ''}
                        </div>
                    ` : ''}
                    ${topicData.topic?.hide_tag_filters !== true && topicData.allUserTags.length > 0 ? html`
                        <h4><translated-span>Tags and Tag Groups</translated-span>
                            ${this.selectedTags.tags.length > 1 ? html`
                                <span class="or-and-toggle-container">
                                    <translated-span class="or-and-toggle toggle-left ${this.selectedTags.operator === CombineOperator.Or ? 'selected' : ''}" @click=${async () => await this.changeTopLevelOperator(CombineOperator.Or)}>
                                        OR
                                    </translated-span>
                                    <translated-span class="or-and-toggle toggle-right ${this.selectedTags.operator === CombineOperator.And ? 'selected' : ''}" @click=${async () => await this.changeTopLevelOperator(CombineOperator.And)}>
                                        AND
                                    </translated-span>
                                </span>
                            ` : ''}
                        </h4>
                        ${this.selectedTags.tags.length > 1 ? html`
                            ${this.selectedTags.operator === CombineOperator.Or
                                ? html`
                                    <translated-span class="sub-info">
                                        Points must match at least one of the outlined groups to match.
                                    </translated-span>`
                                : html`
                                    <translated-span class="sub-info">
                                        Points must match all of the outlined groups to match.
                                    </translated-span>`
                            }`
                            : ''
                        }
                        <div class="chips-container flexContainer tags-container">
                            <div class="top-level-tags-container">
                                ${this.topicTags.filter((t: UserTag) => !t.tags || t.tags.length === 0).map((t: UserTag) => {
                                    const isSelected = this.selectedTags.tags?.some((tag) => tag.topLevelTag === t.name);
                                    const isFirstSelectedTopLevelTag = this.selectedTopTags.length > 0 && this.selectedTopTags[0].topLevelTag === t.name;
                                    const translation = translationData.allTranslations.find((w) => w.original_text === t.name)?.translated_text;
                                    return html`
                                        ${isSelected && !isFirstSelectedTopLevelTag ? html`
                                            <p class="operator">
                                                ${this.selectedTags.operator === CombineOperator.Or ? html`
                                                    <translated-span>${CombineOperator.Or.toUpperCase()}</translated-span>
                                                ` : html`
                                                    <translated-span>${CombineOperator.And.toUpperCase()}</translated-span>
                                                `}
                                                 </p>
                                        ` : ``
                                        }
                                        <div class="top-level-subtags-container ${isSelected ? 'selected' :''}">
                                            <div class="tag__item"
                                                style="${isSelected ? `background-color: ${t.icon}; color: ${getVisibleTextColor(t.icon)}` : `background-color: #fff; border: 2px solid ${t.icon};`}"
                                                label="${translation ?? t.name}"
                                                @click="${(e: any) =>
                                                    this.handleChipClick(
                                                        true,
                                                        t.name
                                                    )}">
                                                ${translation ?? t.name}
                                                <div class="tag__icon">${isSelected ? String.fromCodePoint(0x2D) : String.fromCodePoint(0x2B)}</div>
                                            </div>
                                        </div>
                                    `;
                                    }
                                )}
                            </div>
                            ${this.topicTags.filter((t: UserTag) => t.tags && t.tags.length > 0).map((t: UserTag) => {
                                const operator = (this.selectedTags?.tags?.find((tag: any) => tag.topLevelTag === t.name) as SubTagSearchType)?.operator ?? CombineOperator.Or;
                                const hasSelectedSubTags = this.selectedTags?.tags?.some((tag: any) => tag.topLevelTag === t.name && tag.subTags?.length > 0);
                                const isFirstSubtagGroup = this.topicTags.filter((tag: UserTag) => tag.tags && tag.tags.length > 0).findIndex((tag: UserTag) => tag.name === t.name) === 0;
                                const numSelectedSubtags = this.selectedSubTags?.find((tag: any) => tag.topLevelTag === t.name)?.subTags?.length ?? 0;

                                return html`
                                    ${hasSelectedSubTags && (this.selectedTopTags.length > 0 || !isFirstSubtagGroup)  ? html`
                                        <p class="operator"> ${this.selectedTags.operator === CombineOperator.Or ? html`
                                            <translated-span>${CombineOperator.Or.toUpperCase()}</translated-span>
                                        ` : html`
                                            <translated-span>${CombineOperator.And.toUpperCase()}</translated-span>
                                        `}
                                        ` : ``
                                    }
                                    <span class="tag-group-name"><translated-span>${t.name}</translated-span> <translated-span>Group</translated-span>
                                        ${numSelectedSubtags > 1 ? html`
                                            <span class="subtag-and-or-toggle">
                                                <div class="or-and-toggle-container">
                                                    <translated-span class="or-and-toggle toggle-left ${operator === CombineOperator.Or ? 'selected' : ''}" @click=${async () => await this.changeSubtagOperator(t.name, CombineOperator.Or)}>
                                                        OR
                                                    </translated-span>
                                                    <translated-span class="or-and-toggle toggle-right ${operator === CombineOperator.And ? 'selected' : ''}" @click=${async () => await this.changeSubtagOperator(t.name, CombineOperator.And)}>
                                                        AND
                                                    </translated-span>
                                                </div>
                                            </span>` : ''
                                        }
                                    </span>
                                    ${numSelectedSubtags > 1 ? html`
                                        ${operator === CombineOperator.Or ? html`
                                            <translated-span class="tag-group-name sub-info">
                                                Points must have at least one
                                                of the selected sub-tags in this group to match.
                                            </translated-span>`
                                            : html`
                                                <translated-span class="tag-group-name sub-info">
                                                    Points must have all of the selected sub-tags in this group to match.
                                                </translated-span>`
                                        }
                                    ` : ''
                                    }
                                    <div class="top-level-subtags-container ${hasSelectedSubTags ? 'selected' : ''}">

                                        <div class="subtags-container">
                                            ${t.tags?.map((subtag) => {
                                                const isSelected = this.selectedSubTags?.some((tag) => tag.subTags?.some((sub) => sub === subtag.name));
                                                const isFirstTagInSelectedList = this.selectedSubTags?.some((tag) => tag.topLevelTag === t.name && tag.subTags?.length > 0 && tag.subTags[0] === subtag.name);
                                                const translation = translationData.allTranslations.find((t) => t.original_text === subtag.name)?.translated_text;
                                                return html`
                                                    ${isSelected && !isFirstTagInSelectedList ? html`
                                                        ${operator === CombineOperator.Or ? html`
                                                            <translated-span class="operator subtagoperator ${hasSelectedSubTags ? 'light' : ''}"> ${operator.toUpperCase()} </translated-span>
                                                        ` : html`
                                                            <translated-span class="operator subtagoperator ${hasSelectedSubTags ? 'light' : ''}"> ${operator.toUpperCase()} </translated-span>
                                                        `}
                                                    ` : ''}
                                                    <div class="tag__item"
                                                        style="${isSelected ? `background-color: ${subtag.icon}; color: ${getVisibleTextColor(subtag.icon)}` : `background-color: #fff; border: 2px solid ${subtag.icon};`}"
                                                        label="${translation ?? subtag.name}"
                                                        @click="${(e: any) =>
                                                            this.handleChipClick(
                                                                true,
                                                                subtag.name,
                                                                false,
                                                                true,
                                                                t.name
                                                            )}">
                                                        ${translation ?? subtag.name}
                                                        <div class="tag__icon">${isSelected ? String.fromCodePoint(0x2D) : String.fromCodePoint(0x2B)}</div>
                                                    </div>
                                                `;
                                            })}
                                        </div>
                                    </div>

                                `;
                            })}
                        </div>
                    ` : ''}
                </div>
                <div class="action-buttons">
                    <form-button class="clear-btn" variant="outline" @click="${this.resetFilters}">
                        <translated-span>Clear all filters</translated-span>
                    </form-button>
                    <form-button class="search-btn" variant="custom" @click="${this.submitFilters}">
                        <translated-span>Search</translated-span>
                    </form-button>
                </div>
            </div>
            <error-toast id="errorToast"></error-toast>
        `;
    }

    static get styles() {
        return css`
            :host {
                display: flex;
                flex-direction: column;
            }

            h4 {
                margin: 5px 0;
            }

            .subtag-and-or-toggle {
                display: flex;
                margin: 0.5rem 0;
                flex-direction: row;
                align-items: center;
                justify-content: flex-start;
                margin-left: 5px;
            }

            .subtag-and-or-toggle.light {
                color: var(--theme-white-color);
            }

            .tag__item {
                position: relative;
                user-select: none;
                cursor: pointer;
                display: flex;
                justify-content: center;
                align-items: center;
                padding: 3px 14px;
                border-radius: 1.25rem;
                margin-right: 6px;
                margin-bottom: 4px;
                font-size: 10px;
                border: 1px solid var(--main-primary-color);
            }

            .tag__icon {
                border-radius: 999px;
                width: 1rem;
                height: 1rem;
                font-size: 16px;
                display: flex;
                align-items: center;
                justify-content: center;
                position: absolute;
                top: -5px;
                right: -5px;
                background: var(--theme-white-color);
                border: 1px solid var(--main-primary-color);
                color: var(--main-primary-color);
            }

            .tag-group-name {
                flex: 1 1 100%;
                margin: 0 0 0 10px;
                display: flex;
                flex-direction: row;
                align-items: center;
                gap: 0.5rem;
                text-transform: uppercase;
                font-size: 0.7rem;
            }

            .sub-info {
                font-size: 0.7rem;
                color: var(--theme-input-color);
                text-transform: unset;
                margin-top: 0px;
                line-height: 1;
                margin-bottom: 5px;
                flex: 1 1 100%;
            }

            .or-and-toggle-container {
                padding-left: -5px;
                flex-direction: row;
                align-items: center;
            }

            .or-and-toggle-container.light {
                color: var(--theme-white-color);
            }

            .or-and-toggle {
                cursor: pointer;
                padding: 0.25rem 0.5rem;
                font-size: 0.6rem;
                color: var(--theme-input-color);
                border: 1px solid var(--main-primary-color);
                font-weight: normal;
            }

            .or-and-toggle.light {
                border: 1px solid var(--theme-white-color);
                color: var(--theme-white-color);
            }

            .or-and-toggle.selected {
                color: var(--theme-white-color);
                background-color: var(--main-primary-color);
            }

            .or-and-toggle.selected.light {
                background-color: var(--theme-white-color);
                color: var(--main-primary-color);
            }

            .or-and-toggle.toggle-left {
                border-radius: 0.5rem 0 0 0.5rem;
            }

            .or-and-toggle.toggle-right {
                border-radius: 0 0.5rem 0.5rem 0;
            }

            .modalContent {
                --mdc-icon-size: 18px;
                display: flex;
                flex-direction: column;
                height:100%;
            }

            .modalTitle {
                text-align: center;
                margin-bottom: 1.5rem;
                margin-top: 0;
            }

            .modalBody {
                flex: 1;
                overflow-y: auto;
                padding: 0 1rem;
                overflow-x: hidden;
            }

            .top-level-subtags-container {
                width: 100%;
                background-color: transparent;
                padding: 5px;
                margin-left: 10px;
                border-radius: 5px;
                display: flex;
            }

            .subtags-container {
                display: flex;
                flex-direction: row;
                flex-wrap: wrap;
            }

            .top-level-subtags-container.selected {
                background-color: var(--theme-white-color);
                border: 1px solid var(--main-primary-color);
                margin-left: -10px;
            }

            .modalBody::-webkit-scrollbar {
                width: 5px;
            }

            .modalBody::-webkit-scrollbar-track {
                background: var(--theme-background-color);
            }

            .modalBody::-webkit-scrollbar-thumb {
                background: var(--theme-border-color);
            }

            .flexContainer {
                display: flex;
                align-items: center;
                margin-right: 20px;
            }

            .displayNone {
                display: none;
            }

            .displayBlock {
                display: block;
            }

            form-tooltip {
                margin-top: 3px;
            }

            form-tooltip mwc-icon {
                line-height: unset;
                margin-left: 2px;
            }

            #tagsShowOption {
                margin-right: 5px;
                background: var(--theme-white-color);
                color: var(--main-primary-color);
                height: 2rem;
                border-radius: 999px;
                line-height: 0.8rem;
                display: flex;
                justify-content: space-between;
                align-items: center;
                padding: 0 0.5rem;
                pointer-events: auto;
            }

            #tagsShowOption mwc-icon,
            paper-chip mwc-icon {
                font-size: 20px;
            }

            .tagInfo {
                min-width: 1.25rem;
                text-align: center;
                display: flex;
                align-items: center;
            }

            .divider {
                border-color: var(--theme-border-color);
            }

            .chips-container {
                flex-wrap: wrap;
                margin-bottom: 1.5rem;
            }

            .keywords-container {
                margin: 0.5rem 0;
                flex-wrap: wrap;
            }

            .behavior-toggle {
                margin: 1rem 0;
            }

            .behavior-toggle a {
                text-decoration: underline;
                flex: 1;
            }


            .behavior-options{
                width: 100%;
                display: flex;
                flex-direction: row;
                align-items: flex-start;
                font-size: 0.875rem;
                color: var(--theme-input-color);
            }

            .top-level-tags-container {
                width: 100%;
            }

            .control-item {
                margin-left: 5px;
                margin-right: 5px;
            }

            .form-control {
                display: flex;
                flex-direction: row;
                align-items: center;
                justify-content: flex-start
            }

            .action-buttons {
                display: flex;
                justify-content: flex-end;
                gap: 1rem;
                margin-top: 0.5rem;
            }

            .search-btn {
                --btn-background-color: var(--main-success-color);
                --btn-label-color: white;
            }

            .form-radio-input {
                width: 1rem;
                height: 1rem;
                margin-top: 0.25em;
                vertical-align: top;
                background-color: transparent;
                background-repeat: no-repeat;
                background-position: center;
                background-size: contain;
                border: 2px solid var(--theme-checkbox-color);
                -webkit-appearance: none;
                -moz-appearance: none;
                appearance: none;
                border-radius: 50%;
            }

            .form-radio-input:checked {
                background-color: transparent;
                border-color: var(--main-primary-color);
                background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='-4 -4 8 8'%3e%3ccircle r='2' fill='%23102C3D'/%3e%3c/svg%3e");
            }

            paper-chip.custom-chip {
                --paper-chip-background-color: transparent;
                cursor: pointer;
                pointer-events: all;
                display: flex;
                flex-wrap: nowrap;
                align-items: center;
            }

            .tags-container {
                display: flex;
                flex-direction: column;
                align-items: flex-start;
                border-left: 1px solid var(--main-primary-color);
            }

            .operator {
                margin: 5px;
                margin-left: -10px;
                background: var(--main-primary-color);
                width: unset;
                width: 30px;
                text-align: center;
                color: var(--theme-white-color);
                font-size: 0.6rem;
            }

            .operator.subtagoperator {
                background: transparent;
                color: var(--main-primary-color);
                margin-left: 5px;
            }

            .operator.subtagoperator.light {
                color: var(--main-primary-color);
            }

            @media (max-width: 600px) {
                .modalContent {
                    width: 100%;
                }

                .modalBody {
                    padding: 0;
                    overflow-x: hidden;
                }

                .top-level-tags-container{
                    width: 90%;
                }

                .tags-container {
                    margin-left: 10px;
                }
            }
        `;
    }

    customizeStyles() {
        const chipButtons: any = this?.shadowRoot?.querySelectorAll('paper-chip');
        for (const elem of chipButtons) {
            const chipButton = elem?.shadowRoot?.querySelector('.chip');
            chipButton?.classList.remove('unselectable');

            const chipButtonStyle = elem?.shadowRoot?.querySelector('style');
            chipButtonStyle.innerHTML = `
                .chip {
                    display: flex;
                    flex-direction: row-reverse;
                    height: 32px;
                    font-size: var(--paper-chip-font-size, 13px);
                    font-weight: 500;
                    background-color: var(--paper-chip-background-color);
                    color: var(--paper-chip-secondary-text-color);
                    line-height: 32px;
                    padding: 0 4px 0 12px;
                    border-radius: 16px;
                    margin-bottom: 5px;
                    margin-right: 5px;
                }

                .chip:active {
                    background: var(--theme-border-color);
                }

                .chip .closeIcon {
                    margin-left: 4px;
                    cursor: pointer;
                    float: right;
                    width: 12px;
                }

                .chip .inline {
                    display: -webkit-inline-box;
                }

                .hoverEffect {
                    box-shadow: var(--shadow-elevation-2dp_-_box-shadow);
                    cursor: pointer;
                }

                .unselectable {
                    -webkit-touch-callout: none;
                    -webkit-user-select: none;
                    -khtml-user-select: none;
                    -moz-user-select: none;
                    -ms-user-select: none;
                    user-select: none;
                }

                .close {
                    top: -1px;
                }

                iron-icon {
                    --iron-icon-height: 16px;
                    --iron-icon-width: 16px;
                    position: relative;
                    right: 8px;
                    border-radius: 50%;
                }

                .label {
                    white-space: nowrap;
                    margin-right: 8px;
                }

                .avatar ::slotted(.chip-image) {
                    float: left;
                    margin: 0 8px 0 -12px;
                    height: 32px;
                    width: 32px;
                    border-radius: 50%;
                }

                .avatar ::slotted(.chip-background) {
                    --iron-icon-height: 22px;
                    --iron-icon-width: 22px;
                    border-radius: 50%;
                    float: left;
                    font-weight: bold;
                    font-size: 1rem;
                    width: 2rem;
                    height: 2rem;
                    margin:  0 0px 0 -10px;
                    text-align: center;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                .avatar ::slotted(.mwc-icon) {
                    --iron-icon-height: 22px;
                    --iron-icon-width: 22px;
                    border-radius: 50%;
                    float: left;
                    font-weight: bold;
                    font-size: 1rem;
                    width: 2rem;
                    height: 2rem;
                    margin:  0 8px 0 -10px;
                    text-align: center;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }

                [hidden] {
                    display: none;
                }
            `;
        }
    }
}
